'use client';
import buildRecipeToPrint from '@functions/print/recipePrint';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import {
  IngredientSummaryProps,
  RecipeStepProps,
  RecipeMetaSectionProps,
} from '@repo/ui/src/types';
import { SegmentRecipeProps } from '@components/RecipeNotes/_RecipeNote';
import { recipePrinted } from '@repo/ui/src/services/analytics';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import PrintIcon from '@repo/ui/src/components/Icons/PrintIcon';
import { RecipeTimeProps } from '@views/RecipeDetailPage/_RecipeHero';

interface PrintProps {
  recipeMetaSection: RecipeMetaSectionProps;
  ingredientSummary?: IngredientSummaryProps;
  authorName: string;
  servingSize: number;
  prepTime: Array<RecipeTimeProps>;
  cookTime: Array<RecipeTimeProps>;
  unitOfMeasure: number;
  steps?: Array<RecipeStepProps>;
  segmentRecipe?: SegmentRecipeProps;
  variant?: 'icon' | 'button';
}

const printRecipe = ({
  recipeMetaSection,
  ingredientSummary,
  prepTime,
  cookTime,
  authorName,
  steps,
  servingSize,
  unitOfMeasure,
}: PrintProps) => {
  try {
    const res = buildRecipeToPrint({
      recipeMetaSection,
      ingredientSummary,
      prepTime,
      cookTime,
      authorName,
      steps,
      servingSize,
      unitOfMeasure,
    });
    if (res) {
      const wnd = window.open();
      if (wnd) {
        wnd.document.write(res);
        wnd.print();
      }
    }
  } catch (error) {
    console.error(error);
  }
};

const PrintButton = ({
  recipeMetaSection,
  servingSize,
  authorName,
  ingredientSummary,
  unitOfMeasure,
  prepTime,
  cookTime,
  steps,
  segmentRecipe,
  variant = 'button',
}: PrintProps) => {
  const { t } = useTranslation();
  const handlePrint = () => {
    printRecipe({
      recipeMetaSection,
      ingredientSummary,
      prepTime,
      cookTime,
      authorName,
      steps,
      servingSize,
      unitOfMeasure,
      segmentRecipe,
    });
    recipePrinted({
      ...segmentRecipe,
      pellet_recommendation: segmentRecipe?.pellet_recommendation ?? null,
      recipe_id: '',
      recipe_author_name: segmentRecipe?.recipe_author_name ?? null,
      recipe_name: segmentRecipe?.recipe_name ?? '',
      recipe_url: segmentRecipe?.recipe_url ?? '',
      recipe_image_url: segmentRecipe?.recipe_image_url ?? null,
      recipe_meat_type: segmentRecipe?.recipe_type ?? null,
      recipe_type: segmentRecipe?.recipe_type ?? undefined,
      recipe_cook_time: segmentRecipe?.recipe_cook_time ?? null,
      recipe_measurement_units: segmentRecipe?.recipe_measurement_units ?? '',
      recipe_prep_time: segmentRecipe?.recipe_prep_time ?? null,
      recipe_serving_size: segmentRecipe?.recipe_serving_size ?? null,
    });
  };

  return variant === 'icon' ? (
    <div className="relative pt-5 md:pt-0">
      <button
        className="flex justify-center bg-warm-300 rounded-full hover:bg-brand-300 hover:text-warm-100 focus:text-brand-400"
        onClick={handlePrint}
      >
        <PrintIcon />
      </button>
    </div>
  ) : (
    <div className="flex">
      <Button
        variant={ButtonVariant.secondary}
        onClick={handlePrint}
        className="flex justify-center w-full border-2 items-center py-2 sm:px-5 lg:px-10 rounded"
      >
        <PrintIcon />
        <p className="tra-text-web-utl-button-m mb-0 ml-2">
          {t('recipeContent.printLabel', undefined, { default: 'Print Recipe' })}
        </p>
      </Button>
    </div>
  );
};

export default PrintButton;
